import { AppStore } from "@/store";
import { resolveNoteSuggestionBaseContent } from "@/store/note/helpers/note-suggestion-content";
import { processAudioRecordingRefine } from "@/store/audio/transforming/audio-recording-transformer.utils";
import { noteModule } from "@mem-labs/common-editor";
import { runInAction } from "mobx";
import { UpsertNoteSuggestionOperation } from "@/store/sync/operations/notes/UpsertNoteSuggestionOperation";
import { uuidModule } from "@/modules/uuid";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export const refineNoteWithTranscript = async ({
  noteId,
  audioRecordingId,
  capturedAudioId,
  transcriptContent,
  audioDurationSeconds,
  userPrompt,
  associatedSessionId,
  activeSuggestionMarkdownContent,
  store,
}: {
  noteId: string;
  audioRecordingId: string;
  capturedAudioId: string;
  transcriptContent: string;
  audioDurationSeconds?: number | null;
  userPrompt?: string;
  associatedSessionId?: string;
  activeSuggestionMarkdownContent?: string;
  store: AppStore;
}): Promise<{ success: true } | { success: false; error: string }> => {
  const targetUserPrompt = userPrompt || "Refine note with transcript";

  try {
    const note = await store.notes.getAsync(noteId);
    if (!note) return { success: false, error: "Note not found" };

    runInAction(() => (note.isProcessingSuggestion = true));

    try {
      const {
        encodedContent: prevEncodedContent,
        markdownContent: prevMarkdownContent,
        primaryLabel: prevPrimaryLabel,
        secondaryLabel: prevSecondaryLabel,
      } = await resolveNoteSuggestionBaseContent(note);
      const targetRefineMarkdownContent = activeSuggestionMarkdownContent ?? prevMarkdownContent;

      const targetSessionId =
        associatedSessionId ||
        uuidModule.resolveCapturedAudioSessionSyncModelUuid({
          capturedAudioId,
        });

      const { suggestedMarkdownContent } = await processAudioRecordingRefine({
        audioRecordingId,
        transcriptContent,
        associatedNoteMarkdownContent: targetRefineMarkdownContent,
        associatedSessionId: targetSessionId,
        userPrompt: targetUserPrompt,
        audioDurationSeconds,
      });

      const {
        suggestedEncodedContent,
        suggestedEncodedContentDiff,
        suggestedPrimaryLabel,
        suggestedSecondaryLabel,
      } = noteModule.buildSuggestionData({
        previousEncodedContent: prevEncodedContent,
        suggestedMarkdownContent,
      });
      const suggestionId = uuidModule.generate();

      await new UpsertNoteSuggestionOperation({
        store,
        payload: {
          suggestion_id: suggestionId,
          note_id: noteId,
          kind: "VOICE_SESSION_REFINE_V1",
          prev_encoded_content: prevEncodedContent,
          prev_primary_label: prevPrimaryLabel,
          prev_secondary_label: prevSecondaryLabel,
          prev_markdown_content: prevMarkdownContent,
          suggested_encoded_content: suggestedEncodedContent,
          suggested_encoded_content_diff: suggestedEncodedContentDiff,
          suggested_primary_label: suggestedPrimaryLabel,
          suggested_secondary_label: suggestedSecondaryLabel,
          suggested_markdown_content: suggestedMarkdownContent,
          metadata: {
            session_id: targetSessionId,
            captured_audio_id: capturedAudioId,
            audio_recording_id: audioRecordingId,
            transcript_text: transcriptContent,
            user_prompt: targetUserPrompt,
          },
        },
      }).execute();

      return { success: true };
    } finally {
      runInAction(() => (note.isProcessingSuggestion = false));
    }
  } catch (e: unknown) {
    logger.error({
      message: "Failed to refine note with transcript",
      info: {
        error: objectModule.safeErrorAsJson(e as Error),
        noteId,
        audioRecordingId,
        capturedAudioId,
        userPrompt: targetUserPrompt,
      },
    });
    return { success: false, error: e instanceof Error ? e.message : "Unknown error" };
  }
};
